// =============================================================================
// GateBanner - error banner with a smooth upgrade path on plan/billing gates
// =============================================================================
// Every feature gate used to dead-end in a bare critical banner ("Subscription
// required for this feature") with no way forward. This shared banner detects
// the typed gate errors preserved by ApiError (402 BILLING_REQUIRED / 403
// FEATURE_NOT_AVAILABLE, with requiredPlan) and renders a warning banner with a
// "View plans" action that navigates to the Billing page. Any other error
// renders as the usual critical banner. Works inline and inside modals.

import { Banner } from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../i18n/LanguageContext';

/** What the pages store in their error state: a plain message or the caught
 *  error object (ApiError keeps code/requiredPlan; plain Error just message). */
export type PageError =
  | string
  | (Error & { code?: string; requiredPlan?: string; currentPlan?: string })
  | null;

const GATE_CODES = new Set(['BILLING_REQUIRED', 'FEATURE_NOT_AVAILABLE']);

export function GateBanner({ error, onDismiss }: { error: NonNullable<PageError>; onDismiss?: () => void }) {
  const t = useTranslation();
  const navigate = useNavigate();

  const err = typeof error === 'string' ? null : error;
  if (err?.code && GATE_CODES.has(err.code)) {
    const plan = err.requiredPlan || 'STARTER';
    const title =
      err.code === 'BILLING_REQUIRED'
        ? t.common.gateNeedsSubscription
        : t.common.gateNeedsPlan.replace('{plan}', plan);
    return (
      <Banner
        tone="warning"
        title={title}
        action={{ content: t.common.gateCta, onAction: () => navigate('/billing') }}
        onDismiss={onDismiss}
      >
        <p>{t.common.gateBody.replace('{plan}', plan)}</p>
      </Banner>
    );
  }

  return (
    <Banner tone="critical" onDismiss={onDismiss}>
      <p>{typeof error === 'string' ? error : error.message}</p>
    </Banner>
  );
}
